import { z } from "zod"
import type { DocumentTypeKey, JsonObject, JsonValue } from "../primitives.js"
import type { ReviewFlagType } from "../taxonomy.js"
import {
  workflowReconciliationStatuses,
  type WorkflowArrivalSource,
  type WorkflowReconciliationStatus,
} from "./vocabulary.js"

/**
 * Everything a rule's checks can read about one document, loaded once per run.
 *
 * Every field is nullable, including the flags, because extraction does not
 * always find a value and a loader does not always manage to read one. A check
 * against a value we do not have has to fail rather than guess: this feature
 * writes into a customer's books, so an unknown is never a pass. The evaluator
 * never reaches past this object, which is what makes it a pure function and
 * the interesting cases cheap to test.
 */
export type WorkflowDocumentFacts = {
  readonly documentId: string
  readonly documentTypeKey: DocumentTypeKey | null
  /**
   * Open review flags on the document. Null means we could not read them, which
   * is deliberately not the same as an empty list: "no unresolved flags" is the
   * check standing between a broken extraction and a customer's books, and it
   * must never pass because a query came back empty.
   */
  readonly openFlagTypes: readonly string[] | null
  /** Derived from the list above, so the two can never disagree. */
  readonly openFlagCount: number | null
  readonly totalAmount: number | null
  readonly currency: string | null
  readonly counterpartyName: string | null
  /** An ISO calendar date, yyyy-mm-dd. */
  readonly documentDate: string | null
  readonly reconciliationStatus: WorkflowReconciliationStatus | null
  readonly arrivalSource: WorkflowArrivalSource | null
  readonly pageCount: number | null
  /**
   * The distinct categories on the document's transaction rows, with names
   * so run history can say "payroll" rather than print an id. Null means
   * they could not be read; an empty list means the rows were read and none
   * is categorised, which is knowledge, not absence.
   */
  readonly rowCategories: readonly WorkflowRowCategory[] | null
}

export type WorkflowRowCategory = {
  readonly id: string
  readonly name: string
}

const numberFactSchema = z.number()
const stringFactSchema = z.string()

function toNumberOrNull(value: JsonValue | undefined): number | null {
  const numeric = numberFactSchema.safeParse(value)
  if (numeric.success) {
    return Number.isFinite(numeric.data) ? numeric.data : null
  }

  const text = stringFactSchema.safeParse(value)
  if (text.success && text.data.trim() !== "") {
    const parsed = Number(text.data)

    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

function toTrimmedStringOrNull(value: JsonValue | undefined): string | null {
  const parsed = stringFactSchema.safeParse(value)
  if (!parsed.success) {
    return null
  }

  const trimmed = parsed.data.trim()

  return trimmed === "" ? null : trimmed
}

/**
 * How a document arrived, in the three words a customer would use. The legacy
 * backfill source is deliberately none of them: it reads as unknown, and every
 * check against it fails rather than claiming somebody uploaded it.
 */
export function toWorkflowArrivalSource(
  batchSource: string | null | undefined
): WorkflowArrivalSource | null {
  switch (batchSource) {
    case "dashboard_upload":
      return "upload"
    case "email_upload":
      return "email"
    case "api_upload":
      return "api"
    case "portal_upload":
      return "upload"
    default:
      return null
  }
}

const reconciliationStatusSchema = z.enum(workflowReconciliationStatuses)

function toReconciliationStatus(
  value: JsonValue | undefined
): WorkflowReconciliationStatus | null {
  return reconciliationStatusSchema.safeParse(value).data ?? null
}

export type WorkflowDocumentFactsInput = {
  readonly documentId: string
  readonly documentTypeKey: DocumentTypeKey | null
  /** Null when the flags could not be read. Never substitute an empty list. */
  readonly openFlagTypes: readonly ReviewFlagType[] | null
  readonly batchSource: string | null
  /** Billed pages on the document, null when nothing has been billed yet. */
  readonly pageCount: number | null
  /** Null when the enrichments could not be read; empty means none set. */
  readonly rowCategories: readonly WorkflowRowCategory[] | null
  /** The document_summary_projections row, or null when there is not one yet. */
  readonly projection: {
    readonly total: string | number | null
    readonly currency: string | null
    readonly counterpartyName: string | null
    readonly documentDate: string | null
    readonly periodStart: string | null
    readonly periodEnd: string | null
    /**
     * The projection's summary jsonb, exactly as stored. Untyped on purpose:
     * the column is untyped, every read below is guarded, and pretending it has
     * a shape here would only move the lie.
     */
    readonly summary: JsonObject
  } | null
}

/**
 * Builds the facts from the projection the rest of the product already reads.
 *
 * One builder, used by the engine and by the dry run, so what the test button
 * shows a customer is what a real run would see. Three decisions live here
 * rather than in the evaluator, because all three are about where a value is
 * kept rather than about how it compares:
 *
 * - a statement's amount is its transaction total and nothing else. The
 *   projection's `total` column holds the closing balance on a statement, so
 *   falling back to it would compare "total under $500" against a balance and
 *   quietly answer a different question;
 * - a statement's date is its period end, since a statement has no single date;
 * - every other type keeps its real date in `period_start`. The extractors map
 *   invoice_date, transaction_date and order_date onto that column and none of
 *   them writes `document_date` at all, so reading `document_date` alone would
 *   make every date check on an invoice or a receipt permanently false. An
 *   invoice's `period_end` is its due date, which is why the fallback is
 *   `period_start` rather than whichever of the two happens to be set.
 */
export function buildWorkflowDocumentFacts(
  input: WorkflowDocumentFactsInput
): WorkflowDocumentFacts {
  const projection = input.projection
  const summary = projection?.summary ?? {}
  const isStatement = input.documentTypeKey === "bank_statement"

  const totalAmount = isStatement
    ? toNumberOrNull(summary.transaction_total)
    : toNumberOrNull(projection?.total ?? null)

  const documentDate =
    toTrimmedStringOrNull(projection?.documentDate ?? null) ??
    toTrimmedStringOrNull(
      (isStatement ? projection?.periodEnd : projection?.periodStart) ?? null
    )

  const currency =
    toTrimmedStringOrNull(projection?.currency ?? null) ??
    toTrimmedStringOrNull(summary.currency)

  return {
    documentId: input.documentId,
    documentTypeKey: input.documentTypeKey,
    openFlagTypes: input.openFlagTypes,
    openFlagCount:
      input.openFlagTypes === null ? null : input.openFlagTypes.length,
    totalAmount,
    currency: currency === null ? null : currency.toUpperCase(),
    counterpartyName: toTrimmedStringOrNull(
      projection?.counterpartyName ?? null
    ),
    documentDate,
    reconciliationStatus: isStatement
      ? toReconciliationStatus(summary.reconciliation_status)
      : null,
    arrivalSource: toWorkflowArrivalSource(input.batchSource),
    pageCount:
      input.pageCount === null || input.pageCount < 0 ? null : input.pageCount,
    rowCategories: input.rowCategories,
  }
}
